export const meta = {
  title: 'Privacy Policy | Hotel Lobby AI',
  description:
    'How Hotel Lobby AI handles your photos, generated videos, account, payment and usage information.',
  updated_at: '2026-10-03',
};

## About this policy

Hotel Lobby AI operates hotel-lobby.org, an on-site service that turns two photos into a two-person AI duet video. This policy explains what information we process, why, who we share it with and the choices you have. Contact us at [support@hotel-lobby.org](mailto:support@hotel-lobby.org).

## Photos and styling notes

When you press the preview or generate button, your two photos are resized in your browser and sent, together with any optional styling note, to our AI processing provider (fal.ai) to create the scene and the video. Photos are not uploaded before you press the button.

- We do **not** keep a copy of your original photos on our servers. They are used for that generation request only and handled by fal.ai under its own data terms.
- Styling notes are checked by our own filter and by an automated prompt-screening service operated by Waffo before generation. Only the note text is sent for screening — not your photos — and the screening service does not store it.
- Only upload photos of yourself or of people who have given you permission. See our [Acceptable Use Policy](/acceptable-use-policy).

## Generated content

Scene images and finished videos are stored so you can view and download them in **My videos**. Videos are kept in our cloud storage (Cloudflare R2) while your account is open. You can ask us to delete specific videos or your whole account at any time.

## Account and usage information

- **Account:** email address, name and profile picture (if you sign in with Google), and login sessions.
- **Usage records:** credits, generation history, orders and support tickets.
- **Technical data:** IP address, browser and device details, approximate language, referral source and request logs, used to run, secure and prevent abuse of generation requests.
- **Emails:** we send transactional emails such as a welcome message after sign-up. These are delivered by our email provider (Resend).

## Payments

Payments are processed by our payment processor or merchant of record (currently PayPal). They receive the payment details you enter; we do not see or store your full card or bank details. We keep order records — amount, product, status and the processor's reference numbers — for accounting, refunds and fraud prevention.

## Analytics

We use Microsoft Clarity to understand how visitors use the site. Clarity records interactions such as clicks, scrolling and page views, and may produce session replays and heatmaps. Clarity masks text you type into form fields by default and uses cookies or similar technologies. See the [Microsoft Privacy Statement](https://privacy.microsoft.com/privacystatement) for details.

## Who we share information with

We share information only with service providers that help us run the service — AI processing (fal.ai), prompt screening (Waffo), hosting and storage (Cloudflare), email delivery (Resend), payments (PayPal) and analytics (Microsoft Clarity) — and only as needed for those purposes. We do not sell your personal information. We may disclose information when required by law, and we report child sexual abuse material to the relevant authorities.

## Retention

We keep account information and generated videos while your account is open. Order and payment records are retained as long as required for tax and accounting obligations. Server logs are kept for a limited period for security. When you delete your account, we delete your videos and profile within 30 days, except for records we must keep by law.

## Your rights and choices

You can access, correct or delete your information, or ask for a copy of it, by emailing [support@hotel-lobby.org](mailto:support@hotel-lobby.org) from your account email. You can also object to certain processing or withdraw consent where applicable. Depending on where you live, you may have the right to complain to your data protection authority.

## Children

Accounts are for adults only. Parents or legal guardians may upload a photo of their own child for wholesome family content, as described in our [Acceptable Use Policy](/acceptable-use-policy). We do not knowingly collect information directly from children.

## Changes

We may update this policy when the service or our data practices change. The date at the top shows the latest revision.
